import { lazy, Suspense, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { AnimatePresence } from "framer-motion";
import { Route, Router, Switch, useLocation } from 'wouter';
import { ThemeProvider } from '@material-tailwind/react';
import 'core-js/stable';

import appConfig from "../../app.config";
import { useAppDispatch, useAppSelector } from '../state/hooks';
import { store } from '../state/store';
import { selectPlayers, selectStations } from '../state/selectors';
import { login, logout } from '../slices/local/user';
import { setConnectionAvailability } from '../slices/local/connection';
import { setChannelData, setStationId, start, WebId } from "../slices/local/orchestrator";
import { hasTMSSession, isKnownUser, toggleKnownUser, useLazyGetCustomerDataQuery } from '../slices/api/auth';
import { useGetStatusQuery } from '../slices/api/cube';
import { show as showTour } from '../slices/local/tour';
import { getStationName } from '../helpers/get-station-name';
import { transformTmsCubeDataToChannels } from "../helpers/transform-tms-cube-data";
import { getAndClearAutoResumePlayback } from '../helpers/auto-resume';
import { ModalProvider } from '../contexts/modal';
import { TourProvider } from '../contexts/tour';

import customTheme from '@brand/theme/material-tailwind.config';
import { MainFrame } from '@brand/MainFrame';
import { Default as DefaultFooter } from '@brand/footers/Default';
import { Volume as VolumeFooter } from '@brand/footers/Volume';
import { Tour } from '../views/partials/Tour';
import { VersionWatcher } from '../views/partials/VersionWatcher';
import { PlaybackIssueWatcher } from '../views/partials/PlaybackIssueWatcher';
import { getSentry } from '../helpers/get-sentry';

import '@brand/styles/main.css';

import '../i18n/config.js'
import { t } from 'i18next';

const Login = lazy(() => import('@brand/Login').then(module => ({ default: module.Login })));
const PlayerOverview = lazy(() => import('@brand/main/PlayerOverview').then(module => ({ default: module.PlayerOverview })));
const Player = lazy(() => import('@brand/main/Player').then(module => ({ default: module.Player })));
const Station = lazy(() => import('@brand/main/Station').then(module => ({ default: module.Station })));

getSentry().init(appConfig.sentry);
console.log(`WebUI: Sentry is ${appConfig.sentry.enabled ? 'enabled' : 'disabled'}`);
window.Sentry = getSentry();

const isChannelSelectorValid = (stations, players, { playerId, channel, stationId }) => {
  if (!Object.values(players).some(p => p.id == playerId))
    return false;

  if (!Object.values(players[playerId].channels).some(c => c.channel == channel))
    return false;

  if (!stationId)
    return true;

  return stations.some(s => s.id == stationId);
}

const DefaultView = ({ user, players, maybeAlreadyLoggedIn }) => {
  const playerList = Object.values(players);

  if (user.loggedIn || maybeAlreadyLoggedIn) {
    if (playerList.length === 1) {
      const player = playerList[0];
      const channel = Math.min(...Object.keys(player.channels).map(Number));

      return (
        <MainFrame
          breadcrumb={[{
            title: player.name
          }]}
          footer={<VolumeFooter playerId={player.id} channel={channel} />}
          customerData={user.customerData}
        >
          <Player playerId={player.id} channel={channel} />
        </MainFrame>
      );
    }

    return (
      <MainFrame
        breadcrumb={[{
          title: t('navbar.player_overview')
        }]}
        footer={<DefaultFooter />}
        customerData={user.customerData}
      >
        <PlayerOverview />
      </MainFrame>
    );
  }

  return <Login />;
};

const App = ({ location, players, stations, children, customerData }) => (
  <div autoFocus={true} className="app-wrapper select-none">
    <ThemeProvider value={customTheme}>
      <ModalProvider>
        <TourProvider>
          <Tour />
          <VersionWatcher />
          <PlaybackIssueWatcher />
          <Router>
            <Suspense fallback={<div className="app-wrapper" />}>
              <AnimatePresence mode="wait">
                <Switch>
                <Route path="/:playerId/:channel" key={location}>
                  {params => isChannelSelectorValid(stations, players, params) ? (
                    <MainFrame
                      breadcrumb={[...(Object.values(players).length > 1
                        ? [{
                          title: t('navbar.player_overview'),
                          href: '/'
                        }]
                        : []), {
                        title: players[params.playerId]?.name || t('navbar.loading')
                      }]}
                      footer={<VolumeFooter
                        playerId={parseInt(params.playerId)}
                        channel={parseInt(params.channel)}
                      />}
                      customerData={customerData}
                    >
                      <Player {...params} />
                    </MainFrame>
                  ) : (
                    children
                  )}
                </Route>
                <Route path="/:playerId/:channel/:stationId" key={location}>
                  {params => isChannelSelectorValid(stations, players, params) ? (
                    <MainFrame
                      breadcrumb={[...(Object.values(players).length > 1
                        ? [{
                          title: t('navbar.player_overview'),
                          href: '/'
                        }]
                        : []), {
                        title: players[params.playerId]?.name || t('navbar.loading'),
                        href: `/${params.playerId}/${params.channel}`
                      }, {
                        title: getStationName(stations.find(station => station.id == params.stationId)) || t('navbar.loading')
                      }]}
                      footer={<VolumeFooter
                        playerId={parseInt(params.playerId)}
                        channel={parseInt(params.channel)}
                      />}
                      customerData={customerData}
                    >
                      <Station {...params} />
                    </MainFrame>
                  ) : (
                    children
                  )}
                </Route>
                <Route path="/logout" key={location}>
                  <Login logout={true} />
                </Route>
                <Route key={location}>
                  {children}
                </Route>
                </Switch>
              </AnimatePresence>
            </Suspense>
          </Router>
        </TourProvider>
      </ModalProvider>
    </ThemeProvider>
  </div>
);

const AppWrapper = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state?.user);
  const players = useAppSelector(selectPlayers);
  const [getCustomerData, customerData] = useLazyGetCustomerDataQuery();
  const [maybeAlreadyLoggedIn, setMaybeAlreadyLoggedIn] = useState(!user.loggedIn && hasTMSSession());
  const [location, navigate] = useLocation();
  const stations = useAppSelector(selectStations);
  const [connectionAvailable, setConnectionAvaialble] = useState(true);

  const cubesEnabled = appConfig.features.cubes;

  const { data: statusData, inProgress, online } = useGetStatusQuery(undefined, {
    skip: !cubesEnabled,
    pollingInterval: appConfig.getStatusInterval,
    selectFromResult: result => ({
      data: result.data,
      inProgress: result.isFetching || result.isLoading,
      online: result.isSuccess
    })
  });

  useEffect(() => {
    if (!cubesEnabled) {
      setConnectionAvaialble(true);
      return;
    }

    if (!inProgress) {
      setConnectionAvaialble(online);
    }
  }, [cubesEnabled, inProgress, online]);

  useEffect(() => {
    if (cubesEnabled && statusData?.ok === true) {
      const channelData = Object.entries(statusData?.data)
        .reduce((channelData, [playerIdStr, cubeData]) => {
          const playerId = parseInt(playerIdStr);
          return {
            ...channelData,
            [playerId]: {
              playerId,
              channels: transformTmsCubeDataToChannels(cubeData),
              isOnline: !!cubeData.is_online
            }
          };
        }, {});

      dispatch(setChannelData({
        data: channelData
      }));
    }

    dispatch(setConnectionAvailability(connectionAvailable));
  }, [statusData, connectionAvailable]);

  useEffect(() => {
    //
    // If we're "maybe already logged in", because there is a
    // TMS session cookie, check its validity by calling
    // get_customer_data
    //
    if (maybeAlreadyLoggedIn && customerData.isUninitialized) {
      getCustomerData({ noCache: true });
    } else if (customerData.isSuccess) {
      //
      // If the customer data is valid, login the user,
      // otherwise, remove the session and log him out
      //
      if (customerData.data.ok) {
        getSentry().setUser({
          username: customerData.data.data?.Customer?.email || 'unknown'
        });
        dispatch(login(customerData.data.data));
      } else {
        dispatch(logout());
        setMaybeAlreadyLoggedIn(false);
      }
    }
  }, [maybeAlreadyLoggedIn, customerData]);

  // Replace history stack head by players page if still on login/home page
  useEffect(() => {
    if ((user.loggedIn || maybeAlreadyLoggedIn) && !!(location.match(/^(\/(login\/?)?)?$/))) {
      navigate('/', { replace: true });
    }
  }, [user.loggedIn, maybeAlreadyLoggedIn, location]);

  // Show tour the first time this user logged in
  useEffect(() => {
    if (appConfig.showOnboarding && user.loggedIn && !isKnownUser()) {
      toggleKnownUser();
      dispatch(showTour());
    }
  }, [user.loggedIn]);

  // Auto-resume playback if reload was triggered by new version update
  useEffect(() => {
    if (user.loggedIn && players[WebId]) {
      const autoResume = getAndClearAutoResumePlayback();
      if (autoResume && autoResume.stationId) {
        dispatch(setStationId({
          playerId: autoResume.playerId,
          channel: autoResume.channel,
          stationId: autoResume.stationId
        }));
        dispatch(start({
          playerId: autoResume.playerId,
          channel: autoResume.channel
        }));
      }
    }
  }, [user.loggedIn, !!players[WebId]]);

  // @hack We're either logged in or not, otherwise there is still something loading
  // or initializing, so we return a white page
  if (maybeAlreadyLoggedIn && !user.loggedIn) {
    return <></>;
  }

  return (
    <App location={location} loggedIn={user.loggedIn || maybeAlreadyLoggedIn} players={players} stations={stations} language={user.language} customerData={user.customerData}>
      <DefaultView user={user} players={players} maybeAlreadyLoggedIn={maybeAlreadyLoggedIn} />
    </App>
  )
};

const AppContext = ({ children }) => (
  <Provider store={store}>
    {children}
  </Provider>
);

const targetNode = document.getElementById('app');
if (targetNode) {
  const root = createRoot(targetNode);
  root.render((
    <AppContext>
      <AppWrapper />
    </AppContext>
  ));
}
